/**
 * Single source of truth for DepthLogix contact details, social profiles
 * and site metadata.
 *
 * Every phone number, email and social URL here was taken from the
 * existing codebase — nothing is invented. If a detail changes, change
 * it here and it updates everywhere on the site.
 */

export const SITE_URL = "https://depthlogix.com";

export const COMPANY = {
  name: "DepthLogix",
  legalName: "DepthLogix",
  tagline: "Software engineering for businesses that need systems, not just websites.",
};

export const CONTACT = {
  email: "info@depthlogix.com",

  /** Display form, as it already appeared on the site. */
  phoneDisplay: "+1 (236) 314-8300",
  /** E.164, for tel: links. */
  phoneE164: "+12363148300",

  /**
   * ⚠️ REPLACE THIS with the real WhatsApp number.
   *
   * This is currently the US number that was already in the codebase,
   * used as a placeholder. Every WhatsApp button on the site reads from
   * here, so changing this one line updates all of them.
   *
   * Format: country code + number, digits only, no "+", no spaces, no
   * leading zero. Examples:
   *   Pakistan  +92 300 1234567  ->  "923001234567"
   *   UK        +44 7700 900123  ->  "447700900123"
   *   US        +1 713 769 4118  ->  "17137694118"
   *
   * Check it works by opening https://wa.me/<number> in a browser: a
   * valid number shows a "Chat on WhatsApp" page, an invalid one shows
   * "phone number shared via url is invalid".
   */
  whatsappNumber: "12363148300",
};

export const SOCIAL = {
  linkedin: "https://www.linkedin.com/company/depth-logix/",
  facebook: "https://www.facebook.com/share/1F87HUvXYu/",
  instagram: "https://www.instagram.com/depthlogix?igsh=MWJuNGhveXJ6aml2MQ==",
};

/** Default pre-filled WhatsApp message. Professional, not salesy. */
const DEFAULT_WHATSAPP_MESSAGE =
  "Hi DepthLogix, I'd like to discuss a software project. Could we set up a short call?";

/**
 * Build an official WhatsApp click-to-chat URL.
 *
 * https://wa.me/<number>?text=<urlencoded message> is the documented
 * format and resolves correctly on desktop (WhatsApp Web / desktop app)
 * and on mobile (opens the native app).
 *
 * @param {string} [message] optional message to pre-fill
 */
export function whatsappUrl(message = DEFAULT_WHATSAPP_MESSAGE) {
  return `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(message)}`;
}

/** Context-specific opener, e.g. from a service page. */
export function whatsappUrlFor(subject) {
  if (!subject) return whatsappUrl();
  return whatsappUrl(
    `Hi DepthLogix, I'm interested in ${subject}. Could we discuss the details?`
  );
}

export const MAILTO = `mailto:${CONTACT.email}`;
export const TEL = `tel:${CONTACT.phoneE164}`;
